import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Trash2, Share2, Download, Printer, FileSpreadsheet, Pencil } from "lucide-react";
import * as XLSX from "xlsx";
import { api, fmt, idDate, shortDate, typeBadge, totalPax, doughTotals } from "../lib/dimsum";

export const History = ({ teams, refreshKey, onChanged, isAdmin }) => {
  const [recaps, setRecaps] = useState([]);
  const [activePreset, setActivePreset] = useState("");
  const [teamFilter, setTeamFilter] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [showWaMenu, setShowWaMenu] = useState(false);
  const [waFields, setWaFields] = useState({
    tim: true,
    jumbo: true,
    mini: true,
    adonan: true,
    dimsum: true,
  });
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [showDetail, setShowDetail] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);

  const openEdit = (r) => {
    setEditRow(r);
    setEditForm({
      date: r.date,
      m1: (r.members || [])[0] || "",
      m2: (r.members || [])[1] || "",
      notes: r.notes || "",
      dough: (r.dough_items || []).map((d) => ({ dough_type: d.dough_type, kg: d.kg })),
      items: (r.items || []).map((i) => ({ dimsum_type: i.dimsum_type, pax: i.pax })),
    });
  };

  const saveEdit = async () => {
    setSavingEdit(true);
    try {
      await api.put(`/recaps/${editRow.id}`, {
        date: editForm.date,
        members: [editForm.m1, editForm.m2].map((m) => m.trim()).filter(Boolean),
        dough_items: editForm.dough.map((d) => ({ dough_type: d.dough_type, kg: Number(d.kg) || 0 })),
        items: editForm.items.map((i) => ({ dimsum_type: i.dimsum_type, pax: Number(i.pax) || 0 })),
        notes: editForm.notes,
      });
      toast.success("Rekap diperbarui");
      setEditRow(null);
      setEditForm(null);
      onChanged?.();
      load();
    } catch {
      toast.error("Gagal memperbarui rekap");
    } finally {
      setSavingEdit(false);
    }
  };

  const load = () => {
    const params = new URLSearchParams();
    if (start) params.append("start", start);
    if (end) params.append("end", end);
    api.get(`/recaps?${params.toString()}`).then((r) => setRecaps(r.data));
  };

  useEffect(load, [start, end, refreshKey]);

  const nameOptions = useMemo(() => {
    const set = new Set();
    (teams || []).forEach((t) => (t.members || []).forEach((m) => m && m.trim() && set.add(m.trim())));
    recaps.forEach((r) => (r.members || []).forEach((m) => m && m.trim() && set.add(m.trim())));
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [teams, recaps]);

  const filtered = useMemo(() => {
    if (!teamFilter) return recaps;
    const q = teamFilter.trim().toLowerCase();
    return recaps.filter((r) => (r.members || []).some((m) => (m || "").trim().toLowerCase() === q));
  }, [recaps, teamFilter]);

  const rangeActive = Boolean(start && end);

  const isoDate = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };

  const applyPreset = (p) => {
    const now = new Date();
    let s, e;
    if (p === "day") {
      s = new Date(now);
      e = new Date(now);
    } else if (p === "week") {
      const dow = (now.getDay() + 6) % 7; // Senin = 0
      const monday = new Date(now);
      monday.setDate(now.getDate() - dow);
      e = new Date(monday);
      e.setDate(monday.getDate() + 4); // Jumat pekan ini
      s = new Date(e);
      s.setDate(e.getDate() - 7); // Jumat pekan lalu
    } else if (p === "month") {
      s = new Date(now.getFullYear(), now.getMonth(), 1);
      e = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    } else {
      s = new Date(now.getFullYear(), 0, 1);
      e = new Date(now.getFullYear(), 11, 31);
    }
    setStart(isoDate(s));
    setEnd(isoDate(e));
    setActivePreset(p);
  };

  const rows = useMemo(() => {
    return [...filtered].sort((a, b) => (a.date || "").localeCompare(b.date || ""));
  }, [filtered]);

  const del = async (id) => {
    await api.delete(`/recaps/${id}`);
    toast.success("🗑️ Data rekap dihapus");
    load();
    onChanged?.();
  };

  const itemsText = (items) =>
    items.map((i) => `${i.dimsum_type} ${fmt(i.pax)} pax`).join(", ");

  const doughText = (r) =>
    (r.dough_items && r.dough_items.length
      ? r.dough_items.map((d) => `${d.dough_type} ${fmt(d.kg)}kg`).join(", ")
      : `${fmt(r.dough_kg)} kg`);

  const exportExcel = () => {
    const header = rangeActive
      ? "Rentang Tanggal"
      : "Tanggal";
    const data = rows.map((r) => ({
      [header]: r.aggregated ? `${r.date_label || `${start} s/d ${end}`}` : r.date,
      Tim: r.members[0] || r.team_name,
      Anggota: r.members.join(" & "),
      "Jenis Adonan": doughText(r),
      "Adonan Jumbo (kg)": doughTotals(r).jumbo,
      "Adonan Mini (kg)": doughTotals(r).mini,
      "Jenis Dimsum": itemsText(r.items),
      "Total Pax": totalPax(r.items),
      ...(rangeActive ? { "Jumlah Laporan": r.count } : { Catatan: r.notes || "" }),
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Rekap Dimsum");
    const suffix = rangeActive ? `${start}_sd_${end}` : "semua";
    XLSX.writeFile(wb, `rekap-dimsum-${suffix}.xlsx`);
    toast.success("📊 Excel berhasil diunduh");
  };

  const exportCsv = () => {
    const rows = [["Tanggal", "Tim", "Anggota", "Adonan", "Total Kg", "Dimsum", "Total Pax", "Catatan"]];
    filtered.forEach((r) =>
      rows.push([
        r.date,
        r.team_name,
        r.members.join(" & "),
        doughText(r),
        r.dough_kg,
        itemsText(r.items),
        totalPax(r.items),
        r.notes,
      ])
    );
    const csv = rows.map((row) => row.map((c) => `"${c}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "rekap-dimsum.csv";
    a.click();
    toast.success("📄 CSV berhasil diunduh");
  };

  const shareWa = async () => {
    const f = waFields;
    const target = selectedRows.size
      ? rows.filter((r) => selectedRows.has(r.id))
      : rows;
    const pad = (s, n) => (s + " ".repeat(n)).slice(0, n);
    const cell = (code, val, unit) => code.padEnd(7) + fmt(val).padStart(5) + unit;
    let text = "*REKAP PRODUKSI DIMSUM*\n";
    if (rangeActive) text += `${idDate(start)} s/d ${idDate(end)}\n`;
    text += "\n";
    if (target.length === 0) {
      text += "Tidak ada data.\n";
    } else {
      target.forEach((r) => {
        if (f.tim) {
          const when = r.aggregated
            ? `${idDate(start)}–${idDate(end)}`
            : idDate(r.date);
          text += `*${r.team_name}* (${when})\n`;
        }
        if (f.jumbo || f.mini) {
          const t = doughTotals(r);
          const bits = [];
          if (f.jumbo) bits.push(`Jumbo ${fmt(t.jumbo)}kg`);
          if (f.mini) bits.push(`Mini ${fmt(t.mini)}kg`);
          text += bits.join("  ") + "\n";
        }
        if (f.adonan || f.dimsum) {
          const codes = [
            ...new Set([
              ...(r.dough_items || []).map((d) => d.dough_type),
              ...(r.items || []).map((i) => i.dimsum_type),
            ]),
          ];
          text += "```\n";
          if (f.adonan && f.dimsum) {
            text += pad("ADONAN (kg)", 18) + "DIMSUM (pax)\n";
          } else if (f.adonan) {
            text += "ADONAN (kg)\n";
          } else {
            text += "DIMSUM (pax)\n";
          }
          codes.forEach((c) => {
            const kg = (r.dough_items || []).find((d) => d.dough_type === c);
            const px = (r.items || []).find((i) => i.dimsum_type === c);
            const left = f.adonan ? (kg ? cell(c, kg.kg, "kg") : "-") : "";
            const right = f.dimsum ? (px ? cell(c, px.pax, "pax") : "-") : "";
            text += (f.adonan && f.dimsum ? pad(left, 18) + right : left || right) + "\n";
          });
          const tDough = (r.dough_items || []).reduce((s, d) => s + (d.kg || 0), 0);
          const tPax = totalPax(r.items);
          const lT = f.adonan ? cell("TOTAL", tDough, "kg") : "";
          const rT = f.dimsum ? cell("TOTAL", tPax, "pax") : "";
          text += (f.adonan && f.dimsum ? pad(lT, 18) + rT : lT || rT) + "\n";
          text += "```\n";
        }
        text += "\n";
      });
    }
    setShowWaMenu(false);
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      toast.success("📲 Format WhatsApp berhasil disalin!");
    } catch {
      toast.error("Gagal menyalin, coba lagi");
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3 no-print">
        <h2 className="font-display text-2xl sm:text-3xl font-extrabold text-stone-900">
          Riwayat Produksi
        </h2>
        <div className="flex gap-2">
          <div className="relative">
            <button
              data-testid="btn-share-whatsapp"
              onClick={() => setShowWaMenu((v) => !v)}
              className="min-h-[44px] rounded-2xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold px-4 flex items-center gap-2 text-sm active:scale-95 transition-transform"
            >
              <Share2 className="w-4 h-4" /> Salin WA
            </button>
            {showWaMenu && (
              <div
                data-testid="wa-config-menu"
                className="absolute left-0 mt-2 w-64 max-w-[85vw] rounded-2xl border-2 border-stone-200 bg-white shadow-xl p-4 z-40 text-left"
              >
                <p className="font-bold text-sm text-stone-900 mb-1">
                  Pilih data untuk disalin
                </p>
                <p className="text-xs font-semibold text-stone-500 mb-2.5">
                  {selectedRows.size
                    ? `${selectedRows.size} baris terpilih akan disalin`
                    : "Centang baris tabel untuk salin tim tertentu"}
                </p>
                <div className="space-y-2">
                  {[
                    ["tim", "Nama Tim"],
                    ["jumbo", "Adonan Jumbo"],
                    ["mini", "Adonan Mini"],
                    ["adonan", "Jenis Adonan"],
                    ["dimsum", "Jenis Dimsum"],
                  ].map(([key, label]) => (
                    <label
                      key={key}
                      className="flex items-center gap-2.5 text-sm font-semibold text-stone-700 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        data-testid={`wa-field-${key}`}
                        checked={waFields[key]}
                        onChange={(e) =>
                          setWaFields((s) => ({ ...s, [key]: e.target.checked }))
                        }
                        className="w-4 h-4 accent-emerald-600"
                      />
                      {label}
                    </label>
                  ))}
                </div>
                <button
                  data-testid="wa-copy-now"
                  onClick={shareWa}
                  className="mt-3 w-full min-h-[42px] rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm active:scale-95 transition-transform"
                >
                  Salin ke WhatsApp
                </button>
              </div>
            )}
          </div>
          <button
            data-testid="btn-toggle-detail"
            onClick={() => setShowDetail((v) => !v)}
            className="min-h-[44px] rounded-2xl bg-violet-100 hover:bg-violet-200 text-violet-800 font-bold px-4 flex items-center gap-2 text-sm active:scale-95 transition-transform"
          >
            {showDetail ? "Sembunyikan Detail" : "Tampilkan Detail"}
          </button>
          <button
            data-testid="btn-export-excel"
            onClick={exportExcel}
            className="min-h-[44px] rounded-2xl bg-green-700 hover:bg-green-800 text-white font-bold px-4 flex items-center gap-2 text-sm active:scale-95 transition-transform"
          >
            <FileSpreadsheet className="w-4 h-4" /> Excel
          </button>
          <button
            data-testid="btn-export-csv"
            onClick={exportCsv}
            className="min-h-[44px] rounded-2xl bg-stone-100 hover:bg-stone-200 text-stone-800 font-bold px-4 flex items-center gap-2 text-sm"
          >
            <Download className="w-4 h-4" /> CSV
          </button>
          <button
            data-testid="btn-print-recap"
            onClick={() => window.print()}
            className="min-h-[44px] rounded-2xl bg-stone-100 hover:bg-stone-200 text-stone-800 font-bold px-4 flex items-center gap-2 text-sm"
          >
            <Printer className="w-4 h-4" /> Cetak
          </button>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 no-print">
        <div className="flex flex-wrap gap-2">
          {[
            ["day", "Hari"],
            ["week", "Pekan"],
            ["month", "Bulan"],
            ["year", "Tahun"],
          ].map(([p, label]) => (
            <button
              key={p}
              data-testid={`filter-${p}`}
              onClick={() => applyPreset(p)}
              className={`min-h-[44px] rounded-2xl px-4 font-bold text-sm transition-colors ${
                activePreset === p
                  ? "bg-rose-600 text-white shadow-md"
                  : "bg-stone-100 text-stone-700 hover:bg-stone-200"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        <select
          data-testid="history-team-filter"
          value={teamFilter}
          onChange={(e) => setTeamFilter(e.target.value)}
          className="rounded-2xl border-2 border-stone-300 bg-white px-3 py-3 font-semibold focus:border-rose-500 focus:outline-none"
        >
          <option value="">Semua Nama</option>
          {nameOptions.map((nm) => (
            <option key={nm} value={nm}>
              {nm}
            </option>
          ))}
        </select>
        <div className="grid grid-cols-2 gap-3 sm:contents">
        <div>
          <label className="block mb-1 text-xs font-bold uppercase tracking-wider text-stone-500">
            Dari Tanggal
          </label>
          <input
            data-testid="history-date-start"
            type="date"
            value={start}
            onChange={(e) => {
              setStart(e.target.value);
              setActivePreset("");
            }}
            className="w-full rounded-2xl border-2 border-stone-300 bg-white px-3 py-3 font-semibold focus:border-rose-500 focus:outline-none"
          />
        </div>
        <div>
          <label className="block mb-1 text-xs font-bold uppercase tracking-wider text-stone-500">
            Sampai Tanggal
          </label>
          <input
            data-testid="history-date-end"
            type="date"
            value={end}
            onChange={(e) => {
              setEnd(e.target.value);
              setActivePreset("");
            }}
            className="w-full rounded-2xl border-2 border-stone-300 bg-white px-3 py-3 font-semibold focus:border-rose-500 focus:outline-none"
          />
        </div>
        </div>
      </div>

      <div className="w-full overflow-x-auto rounded-3xl border-2 border-stone-200 bg-white">
        <table data-testid="recap-history-table" className="w-full text-left border-collapse text-xs sm:text-base">
          <thead>
            <tr className="bg-stone-100 text-stone-700 text-xs uppercase tracking-wider">
              <th className="py-3 px-2 sm:px-4 font-extrabold no-print">
                <input
                  type="checkbox"
                  data-testid="select-all-rows"
                  checked={rows.length > 0 && selectedRows.size === rows.length}
                  onChange={(e) =>
                    setSelectedRows(
                      e.target.checked ? new Set(rows.map((r) => r.id)) : new Set()
                    )
                  }
                  className="w-4 h-4 accent-emerald-600"
                />
              </th>
              <th className="py-3 px-2 sm:px-4 font-extrabold">Tanggal</th>
              <th className="py-3 px-2 sm:px-4 font-extrabold">Tim</th>
              <th className="py-3 px-2 sm:px-4 font-extrabold">Adonan Jumbo</th>
              <th className="py-3 px-2 sm:px-4 font-extrabold">Adonan Mini</th>
              {showDetail && (
                <>
                  <th className="py-3 px-2 sm:px-4 font-extrabold">Jenis Adonan</th>
                  <th className="py-3 px-2 sm:px-4 font-extrabold">Jenis Dimsum</th>
                </>
              )}
              {showDetail && (
                <th className="py-3 px-2 sm:px-4 font-extrabold">Total Pax</th>
              )}
              <th className="py-3 px-2 sm:px-4 font-extrabold no-print"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr
                key={r.id}
                data-testid="history-table-row"
                className="border-t border-stone-100 hover:bg-rose-50/40 transition-colors"
              >
                <td className="py-3 px-2 sm:px-4 no-print">
                  <input
                    type="checkbox"
                    data-testid={`select-row-${r.id}`}
                    checked={selectedRows.has(r.id)}
                    onChange={() =>
                      setSelectedRows((prev) => {
                        const n = new Set(prev);
                        n.has(r.id) ? n.delete(r.id) : n.add(r.id);
                        return n;
                      })
                    }
                    className="w-4 h-4 accent-emerald-600"
                  />
                </td>
                <td className="py-3 px-2 sm:px-4 font-semibold text-stone-700">
                  {r.aggregated ? (
                    <>
                      <span className="block whitespace-nowrap">{shortDate(start)}</span>
                      <span className="block whitespace-nowrap">s/d {shortDate(end)}</span>
                      <span className="block text-xs text-stone-400">
                        {r.count} laporan digabung
                      </span>
                    </>
                  ) : (
                    <span className="whitespace-nowrap">{shortDate(r.date)}</span>
                  )}
                </td>
                <td className="py-3 px-2 sm:px-4">
                  <p className="font-bold text-stone-900">
                    {r.members[0] || r.team_name}
                  </p>
                  <p className="text-xs text-stone-500">{r.members.join(" & ")}</p>
                </td>
                <td className="py-3 px-2 sm:px-4 font-mono-num font-bold text-orange-700 whitespace-nowrap">
                  {fmt(doughTotals(r).jumbo)} kg
                </td>
                <td className="py-3 px-2 sm:px-4 font-mono-num font-bold text-amber-600 whitespace-nowrap">
                  {fmt(doughTotals(r).mini)} kg
                </td>
                {showDetail && (
                  <>
                    <td className="py-3 px-2 sm:px-4 align-top">
                      <div className="flex flex-col gap-1 items-start">
                        {r.dough_items && r.dough_items.length ? (
                          r.dough_items.map((d, idx) => (
                            <span
                              key={idx}
                              className={`rounded-lg border px-2 py-0.5 text-xs font-bold whitespace-nowrap ${typeBadge(d.dough_type)}`}
                            >
                              {d.dough_type} {fmt(d.kg)} kg
                            </span>
                          ))
                        ) : (
                          <span className="font-mono-num font-bold text-orange-700 whitespace-nowrap">
                            {fmt(r.dough_kg)} kg
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-2 sm:px-4 align-top">
                      <div className="flex flex-col gap-1 items-start">
                        {r.items.map((i, idx) => (
                          <span
                            key={idx}
                            className={`rounded-lg border px-2 py-0.5 text-xs font-bold whitespace-nowrap ${typeBadge(i.dimsum_type)}`}
                          >
                            {i.dimsum_type} {fmt(i.pax)} pax
                          </span>
                        ))}
                      </div>
                    </td>
                  </>
                )}
                {showDetail && (
                  <td className="py-3 px-2 sm:px-4 font-mono-num font-bold text-emerald-700 whitespace-nowrap">
                    {fmt(totalPax(r.items))} pax
                  </td>
                )}
                <td className="py-3 px-2 sm:px-4 no-print">
                  {isAdmin && !r.aggregated && (
                    <div className="flex items-center gap-1.5">
                      <button
                        data-testid="btn-edit-recap-row"
                        onClick={() => openEdit(r)}
                        className="grid place-items-center w-9 h-9 rounded-xl bg-blue-50 hover:bg-blue-100 text-blue-600 transition-colors"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        data-testid="btn-delete-recap-row"
                        onClick={() => del(r.id)}
                        className="grid place-items-center w-9 h-9 rounded-xl bg-rose-50 hover:bg-rose-100 text-rose-600 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={showDetail ? 9 : 6} className="py-10 text-center text-stone-400 font-semibold">
                  Belum ada data rekap.
                </td>
              </tr>
            )}
          </tbody>
          <tfoot>
            {rows.length > 0 && (
              <tr className="border-t-2 border-stone-300 bg-stone-50 font-extrabold text-stone-900">
                <td className="py-3 px-2 sm:px-4 no-print"></td>
                <td className="py-3 px-2 sm:px-4" colSpan={2}>Total</td>
                <td
                  data-testid="history-total-jumbo"
                  className="py-3 px-2 sm:px-4 font-mono-num text-orange-700 whitespace-nowrap"
                >
                  {fmt(rows.reduce((s, r) => s + doughTotals(r).jumbo, 0))} kg
                </td>
                <td
                  data-testid="history-total-mini"
                  className="py-3 px-2 sm:px-4 font-mono-num text-amber-600 whitespace-nowrap"
                >
                  {fmt(rows.reduce((s, r) => s + doughTotals(r).mini, 0))} kg
                </td>
                {showDetail && <td className="py-3 px-2 sm:px-4"></td>}
                {showDetail && <td className="py-3 px-2 sm:px-4"></td>}
                <td
                  data-testid="history-total-all"
                  className="py-3 px-2 sm:px-4 font-mono-num text-stone-900 whitespace-nowrap"
                >
                  {fmt(
                    rows.reduce(
                      (s, r) => s + doughTotals(r).jumbo + doughTotals(r).mini,
                      0
                    )
                  )}{" "}
                  kg
                </td>
                {showDetail && (
                  <td
                    data-testid="history-total-pax"
                    className="py-3 px-2 sm:px-4 font-mono-num text-emerald-700 whitespace-nowrap"
                  >
                    {fmt(rows.reduce((s, r) => s + totalPax(r.items), 0))} pax
                  </td>
                )}
                <td className="py-3 px-2 sm:px-4 no-print"></td>
              </tr>
            )}
          </tfoot>
        </table>
      </div>

      {editRow && editForm && (
        <div
          className="no-print fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
          data-testid="edit-recap-modal"
        >
          <div className="w-full max-w-lg rounded-3xl bg-white shadow-2xl overflow-hidden">
            <div className="flex items-center gap-3 bg-blue-50 border-b-2 border-blue-200 px-6 py-4">
              <div className="grid place-items-center w-11 h-11 rounded-2xl bg-blue-600 text-white">
                <Pencil className="w-5 h-5" />
              </div>
              <p className="font-display font-extrabold text-lg text-stone-900">Edit Rekap Produksi</p>
            </div>

            <div className="px-6 py-4 space-y-3 max-h-[60vh] overflow-auto">
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-stone-500">Tanggal</label>
                <input
                  type="date"
                  data-testid="edit-recap-date"
                  value={editForm.date}
                  onChange={(e) => setEditForm((f) => ({ ...f, date: e.target.value }))}
                  className="w-full min-h-[44px] rounded-xl border-2 border-stone-200 px-3 font-semibold"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-stone-500">Peserta 1</label>
                  <input
                    type="text"
                    data-testid="edit-recap-m1"
                    value={editForm.m1}
                    onChange={(e) => setEditForm((f) => ({ ...f, m1: e.target.value }))}
                    className="w-full min-h-[44px] rounded-xl border-2 border-stone-200 px-3 font-semibold"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-stone-500">Peserta 2</label>
                  <input
                    type="text"
                    data-testid="edit-recap-m2"
                    value={editForm.m2}
                    onChange={(e) => setEditForm((f) => ({ ...f, m2: e.target.value }))}
                    className="w-full min-h-[44px] rounded-xl border-2 border-stone-200 px-3 font-semibold"
                  />
                </div>
              </div>
              {editForm.dough.length > 0 && (
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-stone-500">Adonan (kg)</label>
                  <div className="space-y-2 mt-1">
                    {editForm.dough.map((d, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <span className={`inline-block rounded-lg border px-2 py-0.5 text-xs font-bold min-w-[64px] text-center ${typeBadge(d.dough_type)}`}>
                          {d.dough_type}
                        </span>
                        <input
                          type="number"
                          data-testid={`edit-recap-dough-${d.dough_type}`}
                          value={d.kg}
                          onChange={(e) =>
                            setEditForm((f) => ({
                              ...f,
                              dough: f.dough.map((x, i) => (i === idx ? { ...x, kg: e.target.value } : x)),
                            }))
                          }
                          className="flex-1 min-h-[40px] rounded-xl border-2 border-stone-200 px-3 text-right font-mono-num"
                        />
                        <span className="text-sm font-bold text-stone-400">kg</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {editForm.items.length > 0 && (
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-stone-500">Dimsum (pax)</label>
                  <div className="space-y-2 mt-1">
                    {editForm.items.map((it, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <span className={`inline-block rounded-lg border px-2 py-0.5 text-xs font-bold min-w-[64px] text-center ${typeBadge(it.dimsum_type)}`}>
                          {it.dimsum_type}
                        </span>
                        <input
                          type="number"
                          data-testid={`edit-recap-pax-${it.dimsum_type}`}
                          value={it.pax}
                          onChange={(e) =>
                            setEditForm((f) => ({
                              ...f,
                              items: f.items.map((x, i) => (i === idx ? { ...x, pax: e.target.value } : x)),
                            }))
                          }
                          className="flex-1 min-h-[40px] rounded-xl border-2 border-stone-200 px-3 text-right font-mono-num"
                        />
                        <span className="text-sm font-bold text-stone-400">pax</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-stone-500">Catatan</label>
                <textarea
                  data-testid="edit-recap-notes"
                  value={editForm.notes}
                  onChange={(e) => setEditForm((f) => ({ ...f, notes: e.target.value }))}
                  className="w-full rounded-xl border-2 border-stone-200 px-3 py-2 font-semibold"
                  rows={2}
                />
              </div>
            </div>

            <div className="flex gap-3 px-6 py-4 border-t border-stone-100">
              <button
                data-testid="edit-recap-cancel"
                onClick={() => {
                  setEditRow(null);
                  setEditForm(null);
                }}
                disabled={savingEdit}
                className="flex-1 min-h-[48px] rounded-2xl bg-stone-100 hover:bg-stone-200 text-stone-800 font-bold disabled:opacity-60"
              >
                Batal
              </button>
              <button
                data-testid="edit-recap-save"
                onClick={saveEdit}
                disabled={savingEdit}
                className="flex-1 min-h-[48px] rounded-2xl bg-blue-600 hover:bg-blue-700 text-white font-bold disabled:opacity-60"
              >
                {savingEdit ? "Menyimpan…" : "Simpan Perubahan"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
